import { cn } from "@/lib/utils";
import type { IssueStatus } from "@/types/api";

/**
 * Label and colour for each status. `completed` and `closed` are the pair users
 * confuse — both read as "done" in English — so they get the strongest colour
 * separation. See [[issue-status-pill.spec]].
 *
 * This is the only place a wire status becomes a word, which is what lets
 * `assigned` render as **Pending** without a translation layer at the fetch
 * boundary. The status is a lifecycle state, not an assignee: an issue sits in
 * it with `assigned_to` null, and a row reading "Unassigned · Assigned" says
 * nothing. "Pending" holds either way. The wire value stays `assigned` — it is
 * a backend enum, a filter value, a summary key and a URL param — see
 * `FE-DEBT-018`.
 */
const STATUS_STYLES: Record<
  IssueStatus,
  { label: string; fill: string; dot: string }
> = {
  assigned: {
    label: "Pending",
    fill: "bg-secondary text-secondary-foreground",
    dot: "bg-muted-foreground/60",
  },
  in_progress: {
    label: "In progress",
    fill: "bg-warning text-warning-foreground",
    dot: "bg-warning",
  },
  completed: {
    label: "Completed",
    fill: "bg-tertiary text-tertiary-foreground",
    dot: "bg-tertiary",
  },
  closed: {
    label: "Closed",
    fill: "bg-primary text-primary-foreground",
    dot: "bg-primary",
  },
};

/**
 * Human label for a status, for use in prose rather than in a pill.
 *
 * Falls back to the raw value so a status the client does not know yet is
 * visible rather than blank.
 */
export function issueStatusLabel(status: IssueStatus): string {
  return STATUS_STYLES[status]?.label ?? status;
}

interface IssueStatusPillProps {
  status: IssueStatus;
  /**
   * `dot` renders an unfilled variant — coloured dot plus label — for the
   * status navigation, where four filled pills would fight the selected-chip
   * treatment for attention.
   */
  variant?: "fill" | "dot";
  /**
   * `sm` is the compact chip the issue card's meta line uses, where the pill
   * sits in a row of small type rather than standing on its own.
   */
  size?: "default" | "sm";
  className?: string;
}

export function IssueStatusPill({
  status,
  variant = "fill",
  size = "default",
  className,
}: IssueStatusPillProps) {
  // An unrecognised status still renders: an unknown state should be visible,
  // not invisible. Neutral treatment, raw value as the label.
  const style = STATUS_STYLES[status];
  const label = style?.label ?? status;

  if (variant === "dot") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium",
          className,
        )}
      >
        <span
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            style?.dot ?? "bg-muted-foreground/60",
          )}
          aria-hidden="true"
        />
        {label}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full font-medium",
        size === "sm"
          ? "px-2 py-0.5 text-[10px]"
          : "px-2.5 py-1 text-[11px]",
        style?.fill ?? "bg-secondary text-secondary-foreground",
        className,
      )}
    >
      {label}
    </span>
  );
}
